/*
In-memory index of every Paris tree, used by the cluster-recolour
enrichment pass and by the species mini-map filter.

Why a separate fetch + parse instead of asking MapLibre:
- MapLibre's clustered GeoJSON source exposes querySourceFeatures(), but
  with cluster=true that returns clusters at low zoom and only renders
  tile-loaded features. We need a global iteration over every tree
  regardless of viewport (e.g. to build the species-filtered FC).
- The browser HTTP / SW cache already holds the geojson from the map
  load, so the second fetch is essentially instant — only the parse
  cost (~0.3-1 s desktop, ~1-2 s on a phone) is paid, and only once.

We keep ONLY the lightweight `fFeatures` array (id, sk, remarquable, lat,
lon) — roughly 213k × 5 fields ≈ 25-30 MB. The full parsed GeoJSON
FeatureCollection is dropped right after we project it; iOS Safari
Standalone WebContent is killed around ~200-400 MB on most iPhones, and
keeping a second 50-100 MB object tree on top of MapLibre's own copy
caused the page to go white after a few captures (each capture's setData
rebuilds the cluster index, churning peak memory on top of the steady-
state load). When `applyDiscoveryToFeatures` needs a fresh FC for
setData, we rebuild it from `fFeatures` and let it die after the push.
*/

import type { Feature, FeatureCollection } from 'geojson'

const kAssetPath = '/data/arbres-paris.geojson'  // served by the Pages Function in pwa/functions/data/, which streams the pre-gzipped sibling with Content-Encoding: gzip — browser auto-decodes

export interface ArbreFeature
{
	id: number
	speciesIndex: number
	remarquable: boolean
	lat: number
	lon: number
}

interface RawFeature
{
	geometry: { coordinates: [number, number] }
	properties: {
		id: number
		sk: number
		remarquable: boolean
		discovered?: number
	}
}

interface RawCollection
{
	type: 'FeatureCollection'
	features: RawFeature[]
}

let fFeatures: ArbreFeature[] | null = null  // memoized lightweight array (id/sk/remarquable/lat/lon), null until first parse
let fLoading: Promise<ArbreFeature[]> | null = null  // in-flight loader, dedup concurrent callers

/*
Ensure the trees array is loaded and parsed. Idempotent. Concurrent
callers share a single fetch+parse via fLoading. The full parsed
FeatureCollection is consumed and dropped — only the lightweight
`fFeatures` projection survives the call (see file header for why).
*/
export async function ensureArbresFeatures(): Promise<ArbreFeature[]>
{
	if (fFeatures) return fFeatures
	if (fLoading) return fLoading
	fLoading = (async () => {
		const vResp = await fetch(kAssetPath)  // SW serves cache-first; second hit after the map load is free
		if (!vResp.ok)
		{
			fLoading = null
			throw new Error(`${kAssetPath}: HTTP ${vResp.status}`)
		}
		const vJson = await vResp.json() as RawCollection  // parsed GeoJSON, scoped to this call so it can be GC'd
		const vOut: ArbreFeature[] = new Array(vJson.features.length)  // pre-size for the linear copy
		for (let vI = 0; vI < vJson.features.length; vI++)
		{
			const vRaw = vJson.features[vI]
			vOut[vI] = {
				id: vRaw.properties.id,
				speciesIndex: vRaw.properties.sk,
				remarquable: vRaw.properties.remarquable === true,
				lon: vRaw.geometry.coordinates[0],
				lat: vRaw.geometry.coordinates[1],
			}
		}
		fFeatures = vOut
		fLoading = null
		return vOut
	})()
	return fLoading
}


/*
Build a transient FeatureCollection that stamps every feature's
`properties.discovered` flag (0 or 1). The caller hands this directly to
MapLibre's `setData`, at which point MapLibre rebuilds the cluster index
(so `discovered_count` updates the cluster bucket colours) and we drop our
reference — letting the FC be GC'd in the next idle.

Découverte par espèce (parité Android `enrichGeoJsonWithDiscovery`) :
  - arbre normal    → discovered=1 ssi `sk ∈ inEffectiveSpecies`
  - arbre remarquable → discovered=1 ssi `id ∈ inCapturedRemarquableIds`
`inEffectiveSpecies` doit déjà inclure l'auto-déblocage genre-based
(via `effectivelyCapturedSpecies` côté appelant). `inCapturedRemarquableIds`
peut être l'ensemble des arbreIds capturés : les ids sont uniques, donc pour
une feature remarquable `id ∈ set` ⇔ ce remarquable a été capturé.

Returns null when `fFeatures` isn't loaded yet (caller should defer).
Cost: one allocation of 213k Feature objects + a linear scan, ~80-150 ms
on a phone. Higher peak than the previous in-place mutation, but no
permanent retention — keeping the FC pinned in memory is what was
killing iOS Safari Standalone after a few captures.
*/
export function applyDiscoveryToFeatures(
	inEffectiveSpecies: ReadonlySet<number>,
	inCapturedRemarquableIds: ReadonlySet<number>,
): FeatureCollection | null
{
	if (!fFeatures) return null
	const vOut: Feature[] = new Array(fFeatures.length)
	for (let vI = 0; vI < fFeatures.length; vI++)
	{
		const vF = fFeatures[vI]
		const vDiscovered = vF.remarquable
			? (inCapturedRemarquableIds.has(vF.id) ? 1 : 0)
			: (inEffectiveSpecies.has(vF.speciesIndex) ? 1 : 0)
		vOut[vI] = {
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [vF.lon, vF.lat] },
			properties: {
				id: vF.id,
				sk: vF.speciesIndex,
				remarquable: vF.remarquable,
				discovered: vDiscovered,
			},
		}
	}
	return { type: 'FeatureCollection', features: vOut }
}

/*
Nombre d'arbres ORDINAIRES (non-remarquables) par espèce sur tout le
dataset — miroir du DAO Android `compterArbresOrdinairesParEspece`, agrégé
en une passe et memoizé (≈ 800 entrées, négligeable). Alimente le compteur
« Arbres déverrouillés » (barre Profil + série historique) : un arbre
ordinaire se déverrouille dès que son espèce est capturée, un remarquable
uniquement via sa propre capture — les remarquables sont donc exclus d'ici
pour éviter le double-comptage (parité `ArbreRepository.nombreArbresDecouverts`).
*/
let fOrdinaryCountBySk: Map<number, number> | null = null  // memoized, immutable once built

export async function ordinaryCountsBySpecies(): Promise<ReadonlyMap<number, number>>
{
	if (fOrdinaryCountBySk) return fOrdinaryCountBySk
	const vFeatures = await ensureArbresFeatures()
	const vOut = new Map<number, number>()
	for (const vF of vFeatures)
	{
		if (vF.remarquable) continue
		vOut.set(vF.speciesIndex, (vOut.get(vF.speciesIndex) ?? 0) + 1)
	}
	fOrdinaryCountBySk = vOut
	return vOut
}

/*
Build a stand-alone, discovery-enriched FeatureCollection for a set of
species, plus its [west, south, east, north] bbox — the data the persistent
map's single MapLibre source is swapped to in filtered mode (Android
`map_filtered/{speciesIndices}`). The set is polymorphic like Android's:
a singleton from the fiche-espèce, or `{sk (G, sp.)} ∪ {sks capturés du
genre}` from the fiche genre. Mirrors Android `filterGeoJsonBySpecies` +
`enrichGeoJsonWithDiscovery` fused into one pass:

- keep only features whose `sk` is in `inSpeciesSet` ;
- MASK remarquables the user hasn't captured (parity Android
  `keep = !remarquable || id ∈ capturedRemarquables`) — the hidden-until-
  found contract still holds on the filtered map ;
- stamp `discovered` (0/1) like `applyDiscoveryToFeatures`: normal trees by
  `sk ∈ inEffectiveSpecies`, remarquables by `id ∈ inCapturedRemarquableIds`.

Keeps `properties.sk` (the clustered source's recolour expression keys off
it). Returns null when the set has no visible feature (or `fFeatures`
isn't loaded yet — caller defers).
*/
export function discoveryFCForSpecies(
	inSpeciesSet: ReadonlySet<number>,
	inEffectiveSpecies: ReadonlySet<number>,
	inCapturedRemarquableIds: ReadonlySet<number>,
): {
	collection: FeatureCollection
	bbox: [number, number, number, number]
} | null
{
	if (!fFeatures) return null
	const vMatching: Feature[] = []
	let vWest = Infinity
	let vSouth = Infinity
	let vEast = -Infinity
	let vNorth = -Infinity
	for (const vF of fFeatures)
	{
		if (!inSpeciesSet.has(vF.speciesIndex)) continue
		// Mask remarquables non capturés — ils restent invisibles sur la
		// carte filtrée comme sur la carte pleine tant que non découverts.
		if (vF.remarquable && !inCapturedRemarquableIds.has(vF.id)) continue
		const vDiscovered = vF.remarquable
			? (inCapturedRemarquableIds.has(vF.id) ? 1 : 0)
			: (inEffectiveSpecies.has(vF.speciesIndex) ? 1 : 0)
		vMatching.push({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [vF.lon, vF.lat] },
			properties: { id: vF.id, sk: vF.speciesIndex, remarquable: vF.remarquable, discovered: vDiscovered },
		})
		if (vF.lon < vWest) vWest = vF.lon
		if (vF.lon > vEast) vEast = vF.lon
		if (vF.lat < vSouth) vSouth = vF.lat
		if (vF.lat > vNorth) vNorth = vF.lat
	}
	if (vMatching.length === 0) return null
	return {
		collection: { type: 'FeatureCollection', features: vMatching },
		bbox: [vWest, vSouth, vEast, vNorth],
	}
}
